import {
  Store,
} from 'vuex';

import moment, {
  Moment,
} from 'moment/moment';

import EServiceContainerInstances from '@/module/common/service/ServiceContainer/EServiceContainerInstances';

import IDateTool from '@/module/common/service/UtilsService/contract/IDateTool';
import IServiceContainer from '@/module/common/service/ServiceContainer/IServiceContainer';

class DateTool implements IDateTool {
  private serviceContainer: IServiceContainer;

  private defaultOptions = {
    deleteCurrentYear: false,
    isWeekDay: true,
    time: false,
    timeZone: false,
  };

  constructor(serviceContainer: IServiceContainer) {
    this.serviceContainer = serviceContainer;
  }

  private getOptions = (options: object) => ({
    ...this.defaultOptions,
    ...options,
  });

  private getCurrentDate = (): Moment => moment();

  private getTime = (dateValue: string | Moment): string => moment(dateValue).format('HH:mm');

  private getTimeZone = (dateValue: string | Moment): string => {
    const utcOffset = moment(dateValue).utcOffset() / 60;

    return `GMT ${utcOffset > 0 ? '+' : ''}${utcOffset}`;
  };

  getWeekDay = (dateValue: string | Moment): string => {
    const store = this.serviceContainer.resolve<Store<unknown>>(EServiceContainerInstances.STORE);
    const locale = store.getters.getLocale ?? 'en';

    const dayOfWeekFormat = locale === 'ru' ? 'dd' : 'ddd';
    const dayOfWeek = this.getFormat(dateValue, dayOfWeekFormat);

    return locale === 'ru' ? dayOfWeek.toUpperCase() : dayOfWeek;
  };

  private format = (value: string | Moment, options = {}): string => {
    const dateValue = moment(value);
    const {
      deleteCurrentYear,
      isWeekDay,
      time,
      timeZone,
    } = this.getOptions(options);

    let mappedValue;
    if (deleteCurrentYear && this.getCurrentDate().year() === dateValue.year()) {
      mappedValue = this.formatWithoutYear(dateValue);
    } else {
      mappedValue = this.formatWithYear(dateValue);
    }

    const parts = [
      isWeekDay ? this.getWeekDay(dateValue) : null,
      mappedValue,
    ];

    const timeString = time ? ` ${this.getTime(dateValue)}` : ' ';

    const timeZoneValue = timeZone ? ` ${this.getTimeZone(dateValue)}` : '';

    return (parts.filter((part) => !!part).join(', ') + timeString + timeZoneValue).trim();
  };

  private postFormat = (dateValue: string | Moment): string => {
    const store = this.serviceContainer.resolve<Store<unknown>>(EServiceContainerInstances.STORE);
    const utilsService = this.serviceContainer.utilsService;
    const locale = store.getters.getLocale ?? 'en';

    let newDate = dateValue as string;

    if (locale === 'ru') {
      newDate = utilsService.string.ucFirst(newDate);
    }

    return newDate;
  };

  getFormat = (dateValue: string | Moment, format: string): string => {
    const store = this.serviceContainer.resolve<Store<unknown>>(EServiceContainerInstances.STORE);
    const locale = store.getters.getLocale ?? 'en';

    const formattedDate = locale ? moment(dateValue).locale(locale).format(format) : moment(dateValue).format(format);

    return this.postFormat(formattedDate);
  };

  formatWithYear = (
    dateValue: string | Moment,
  ): string => this.getFormat(dateValue, 'MMM DD, YYYY');

  formatWithoutYear = (
    dateValue: string | Moment,
  ): string => this.getFormat(dateValue, 'MMM DD');

  formatSkipCurrentYear = (
    dateValue: string | Moment,
  ): string => this.format(dateValue, { deleteCurrentYear: true });

  formatSkipCurrentYearWithoutWeekDay = (
    dateValue: string | Moment,
  ): string => this.format(dateValue, {
    deleteCurrentYear: true,
    isWeekDay: false,
  });

  formatWithoutSkipCurrentYear = (
    dateValue: string | Moment,
  ): string => this.format(dateValue);
}

export default DateTool;
